import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect } from "react-router-dom";
import { Routes } from "../routes";

// pages
import Login from "./Login";
import Upgrade from "./Upgrade";
import RoleBasedDashboard from "./dashboard/RoleBasedDashboard";
import Transactions from "./Transactions";
import Settings from "./Settings";
import BootstrapTables from "./tables/BootstrapTables";
import ForgotPassword from "./examples/ForgotPassword";
import ResetPassword from "./examples/ResetPassword";
import NotFoundPage from "./NotFound";
import ServerError from "./ServerError";

// management pages
import Classes from "./management/Classes";
import Students from "./management/Students";
import Teachers from "./management/Teachers";
import Subjects from "./management/Subjects";
import LessonPlans from "./management/LessonPlans";
import Enrollments from "./management/Enrollments";
import SchoolSettings from "./management/SchoolSettings";
import Profile from "./Profile";

// municipal pages
import MunicipalSchools from "./municipal/MunicipalSchools";
import MunicipalStaff from "./municipal/MunicipalStaff";

// teacher pages
import TeacherClasses from "./teacher/TeacherClasses";
import TeacherStudents from "./teacher/TeacherStudents";
import TeacherAttendance from "./teacher/TeacherAttendance";
import TeacherLessonPlans from "./teacher/TeacherLessonPlans";
import TeacherClassContent from "./teacher/TeacherClassContent";

// auth pages
import SetPassword from "./SetPassword";

// documentation pages
import DocsOverview from "./documentation/DocsOverview";
import DocsDownload from "./documentation/DocsDownload";
import DocsQuickStart from "./documentation/DocsQuickStart";
import DocsLicense from "./documentation/DocsLicense";
import DocsFolderStructure from "./documentation/DocsFolderStructure";
import DocsBuild from "./documentation/DocsBuild";
import DocsChangelog from "./documentation/DocsChangelog";

// components
import RoleSidebar from "../components/RoleSidebar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";
import PrivateRoute from "../components/PrivateRoute";

import Accordion from "./components/Accordion";
import Alerts from "./components/Alerts";
import Badges from "./components/Badges";
import Breadcrumbs from "./components/Breadcrumbs";
import Buttons from "./components/Buttons";
import Forms from "./components/Forms";
import Modals from "./components/Modals";
import Navs from "./components/Navs";
import Navbars from "./components/Navbars";
import Pagination from "./components/Pagination";
import Popovers from "./components/Popovers";
import Progress from "./components/Progress";
import Tables from "./components/Tables";
import Tabs from "./components/Tabs";
import Tooltips from "./components/Tooltips";
import Toasts from "./components/Toasts";

const RouteWithLoader = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route {...rest} render={props => ( <> <Preloader show={loaded ? false : true} /> <Component {...props} /> </> ) } />
  );
};

const PrivateRouteWithSidebar = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const localStorageIsSettingsVisible = () => {
    return localStorage.getItem('settingsVisible') === 'false' ? false : true
  }

  const [showSettings, setShowSettings] = useState(localStorageIsSettingsVisible);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
    localStorage.setItem('settingsVisible', !showSettings);
  }

  const WrappedComponent = (props) => (
    <>
      <Preloader show={loaded ? false : true} />
      <RoleSidebar />

      <main className="content">
        <Navbar />
        <div style={{ flexGrow: 1 }}>
          <Component {...props} />
        </div>
        <Footer toggleSettings={toggleSettings} showSettings={showSettings} />
      </main>
    </>
  );

  return (
    <PrivateRoute
      {...rest}
      component={WrappedComponent}
    />
  );
};

const ProtectedHome = () => {
  const token = localStorage.getItem('token');

  if (token) {
    return <Redirect to={Routes.DashboardOverview.path} />;
  }

  return <Redirect to={Routes.Login.path} />;
};

const HomePage = () => (
  <Switch>
    <Route exact path="/" component={ProtectedHome} />
    <RouteWithLoader exact path={Routes.Login.path} component={Login} />
    <RouteWithLoader exact path={Routes.ForgotPassword.path} component={ForgotPassword} />
    <RouteWithLoader exact path={Routes.ResetPassword.path} component={ResetPassword} />
    <RouteWithLoader exact path={Routes.SetPassword.path} component={SetPassword} />
    <RouteWithLoader exact path={Routes.NotFound.path} component={NotFoundPage} />
    <RouteWithLoader exact path={Routes.ServerError.path} component={ServerError} />

    {/* pages */}
    <PrivateRouteWithSidebar exact path={Routes.DashboardOverview.path} component={RoleBasedDashboard} />
    <PrivateRouteWithSidebar exact path={Routes.Upgrade.path} component={Upgrade} />
    <PrivateRouteWithSidebar exact path={Routes.Transactions.path} component={Transactions} />
    <PrivateRouteWithSidebar exact path={Routes.Settings.path} component={Settings} />
    <PrivateRouteWithSidebar exact path={Routes.BootstrapTables.path} component={BootstrapTables} />

    {/* management pages */}
    <PrivateRouteWithSidebar exact path={Routes.Classes.path} component={Classes} />
    <PrivateRouteWithSidebar exact path={Routes.Students.path} component={Students} />
    <PrivateRouteWithSidebar exact path={Routes.Teachers.path} component={Teachers} />
    <PrivateRouteWithSidebar exact path={Routes.Subjects.path} component={Subjects} />
    <PrivateRouteWithSidebar exact path={Routes.CoordinatorLessonPlan.path} component={LessonPlans} />
    <PrivateRouteWithSidebar exact path={Routes.CoordinatorEnrollments.path} component={Enrollments} />
    <PrivateRouteWithSidebar exact path={Routes.SchoolSettings.path} component={SchoolSettings} />
    <PrivateRouteWithSidebar exact path={Routes.Profile.path} component={Profile} />

    {/* municipal pages */}
    <PrivateRouteWithSidebar exact path={Routes.MunicipalSchools.path} component={MunicipalSchools} />
    <PrivateRouteWithSidebar exact path={Routes.MunicipalStaff.path} component={MunicipalStaff} />

    {/* teacher pages */}
    <PrivateRouteWithSidebar exact path={Routes.TeacherClasses.path} component={TeacherClasses} />
    <PrivateRouteWithSidebar exact path={Routes.TeacherStudents.path} component={TeacherStudents} />
    <PrivateRouteWithSidebar exact path={Routes.TeacherClassContent.path} component={TeacherClassContent} />
    <PrivateRouteWithSidebar exact path={Routes.TeacherAttendance.path} component={TeacherAttendance} />
    <PrivateRouteWithSidebar exact path={Routes.TeacherLessonPlans.path} component={TeacherLessonPlans} />

    {/* components */}
    <PrivateRouteWithSidebar exact path={Routes.Accordions.path} component={Accordion} />
    <PrivateRouteWithSidebar exact path={Routes.Alerts.path} component={Alerts} />
    <PrivateRouteWithSidebar exact path={Routes.Badges.path} component={Badges} />
    <PrivateRouteWithSidebar exact path={Routes.Breadcrumbs.path} component={Breadcrumbs} />
    <PrivateRouteWithSidebar exact path={Routes.Buttons.path} component={Buttons} />
    <PrivateRouteWithSidebar exact path={Routes.Forms.path} component={Forms} />
    <PrivateRouteWithSidebar exact path={Routes.Modals.path} component={Modals} />
    <PrivateRouteWithSidebar exact path={Routes.Navs.path} component={Navs} />
    <PrivateRouteWithSidebar exact path={Routes.Navbars.path} component={Navbars} />
    <PrivateRouteWithSidebar exact path={Routes.Pagination.path} component={Pagination} />
    <PrivateRouteWithSidebar exact path={Routes.Popovers.path} component={Popovers} />
    <PrivateRouteWithSidebar exact path={Routes.Progress.path} component={Progress} />
    <PrivateRouteWithSidebar exact path={Routes.Tables.path} component={Tables} />
    <PrivateRouteWithSidebar exact path={Routes.Tabs.path} component={Tabs} />
    <PrivateRouteWithSidebar exact path={Routes.Tooltips.path} component={Tooltips} />
    <PrivateRouteWithSidebar exact path={Routes.Toasts.path} component={Toasts} />

    {/* documentation */}
    <PrivateRouteWithSidebar exact path={Routes.DocsOverview.path} component={DocsOverview} />
    <PrivateRouteWithSidebar exact path={Routes.DocsDownload.path} component={DocsDownload} />
    <PrivateRouteWithSidebar exact path={Routes.DocsQuickStart.path} component={DocsQuickStart} />
    <PrivateRouteWithSidebar exact path={Routes.DocsLicense.path} component={DocsLicense} />
    <PrivateRouteWithSidebar exact path={Routes.DocsFolderStructure.path} component={DocsFolderStructure} />
    <PrivateRouteWithSidebar exact path={Routes.DocsBuild.path} component={DocsBuild} />
    <PrivateRouteWithSidebar exact path={Routes.DocsChangelog.path} component={DocsChangelog} />

    <Redirect to={Routes.NotFound.path} />
  </Switch>
);

export default HomePage;
